import React from "react";
import { ActivityEvent } from "../types";

interface Props {
  event: ActivityEvent;
}

/**
 * The body of a change-history event, rendered inside the activity dialog.
 *
 * Named ChangeDetailExpander until the inline expansion it was written for was
 * replaced by a dialog. It expands nothing and has not for a while; the name
 * was the only thing still describing the old design, which is a reasonable way
 * for the next reader to waste ten minutes looking for a toggle.
 *
 * Renders different content depending on changeSource:
 *   git_commit     — commit SHA (linked), message, parsed subject + detail
 *   snapshot_diff  — subject + detail in a diff-style display
 *   activity_event — activity type + raw details JSON (existing behaviour)
 */
export default function ChangeDetailPanel({ event }: Props) {
  const source = event.changeSource ?? "activity_event";

  if (source === "git_commit") {
    const shortSha = event.commitSha ? event.commitSha.slice(0, 7) : null;
    const commitMessage = (() => {
      if (event.details) {
        try {
          const parsed = JSON.parse(event.details);
          return parsed.commitMessage || "";
        } catch {
          return "";
        }
      }
      return "";
    })();

    return (
      <div className="px-4 py-3 bg-green-50 border-t border-green-100 text-sm space-y-2">
        {shortSha && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs font-medium w-24 shrink-0">Commit</span>
            {event.repoUrl ? (
              <a
                href={event.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-blue-600 hover:underline"
              >
                {shortSha}
              </a>
            ) : (
              <span className="font-mono text-gray-700">{shortSha}</span>
            )}
          </div>
        )}
        {commitMessage && (
          <div className="flex items-start gap-2">
            <span className="text-gray-500 text-xs font-medium w-24 shrink-0 pt-0.5">Message</span>
            <span className="text-gray-800 whitespace-pre-wrap">{commitMessage}</span>
          </div>
        )}
        {event.subject && (
          <div className="flex items-start gap-2">
            <span className="text-gray-500 text-xs font-medium w-24 shrink-0 pt-0.5">Change</span>
            <span className="text-gray-700">
              <span className="font-medium">{event.subject}</span>
              {event.detail ? <span className="text-gray-500"> — {event.detail}</span> : null}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (source === "snapshot_diff") {
    return (
      <div className="px-4 py-3 bg-yellow-50 border-t border-yellow-100 text-sm space-y-2">
        {event.subject && (
          <div className="flex items-start gap-2">
            <span className="text-gray-500 text-xs font-medium w-24 shrink-0 pt-0.5">Subject</span>
            <span className="font-medium text-gray-800">{event.subject}</span>
          </div>
        )}
        {event.detail && (
          <div className="flex items-start gap-2">
            <span className="text-gray-500 text-xs font-medium w-24 shrink-0 pt-0.5">Detail</span>
            <span className="font-mono text-xs text-gray-700 bg-yellow-100 rounded px-2 py-1 whitespace-pre-wrap">
              {event.detail}
            </span>
          </div>
        )}
        {event.changeType && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs font-medium w-24 shrink-0">Type</span>
            <span className="font-mono text-xs text-gray-600">{event.changeType}</span>
          </div>
        )}
      </div>
    );
  }

  // activity_event (default)
  const detailObj = (() => {
    if (!event.details || event.details === "{}") return null;
    try {
      return JSON.parse(event.details);
    } catch {
      return null;
    }
  })();

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-sm space-y-2">
      {event.activityType && (
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs font-medium w-24 shrink-0">Activity</span>
          <span className="text-gray-800">{event.activityType}</span>
        </div>
      )}
      {detailObj && (
        <div className="flex items-start gap-2">
          <span className="text-gray-500 text-xs font-medium w-24 shrink-0 pt-0.5">Details</span>
          <pre className="font-mono text-xs text-gray-700 bg-gray-100 rounded px-2 py-1 whitespace-pre-wrap overflow-auto max-h-48">
            {JSON.stringify(detailObj, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
